import { SubmissionExpiringPolicyData } from '@common/types';
import AutoFixHighOutlined from '@mui/icons-material/AutoFixHighOutlined';
import { useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Box, Grid, IconButton, Stack } from '@common-components';
import { AIFeature, BoxTemplateLabels, FileType } from 'enums';
import { useFormProvider, useSearchTeamInsuranceProduct } from 'hooks';
import { messages } from 'i18n';
import { getFileMetadataField } from 'utils/files-utils';
import FormDatePicker from 'components/hookFormComponents/FormDatePicker';
import FormTextField from 'components/hookFormComponents/FormTextField';
import { DirtyRouteGuard } from 'broker/components/DirtyContentGuard';
import { useGetInsuranceProductsFromTeamProducts } from 'broker/hooks';
import InnerPageLayout from 'broker/pages/SubmissionWorkspacePage/components/InnerPage/InnerPageLayout';
import ProductSelect from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/components/formSteps/Details/components/ProductSelect';
import useGetSelectProducts from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/Quote/components/useGetSelectProducts';
import { useFetchSubmissionFiles } from 'broker/pages/SubmissionWorkspacePage/hooks/useFetchSubmissionFiles';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import { useUiStoreActions } from 'broker/pages/SubmissionWorkspacePage/ui-store/uiStoreProvider';
import { ExpiringPolicyFooter } from './ExpiringPolicyFooter';
import { ExpiringPolicyFormData, formFieldsConfig, schema } from './utils';

export function ExpiringPolicyContent() {
  const { partialSubmission, markets: submissionMarkets } = useSubmissionsWorkspace();
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const expiringPolicyData = useMemo(
    () => partialSubmission?.additionalData?.expiringPolicyData?.[0] || {},
    [partialSubmission?.additionalData?.expiringPolicyData],
  );

  const { items: teamProducts, isLoading: productsLoading } = useSearchTeamInsuranceProduct();
  const products = useGetInsuranceProductsFromTeamProducts(teamProducts);
  const selectProducts = useGetSelectProducts({ submissionMarkets, insuranceProducts: products });
  const { triggerAIAssistant } = useUiStoreActions();
  const { items: submissionFiles, isLoading: isLoadingSubmissionFiles } = useFetchSubmissionFiles();

  const expiringPolicyFiles = submissionFiles
    .filter((file) => getFileMetadataField(file, BoxTemplateLabels.FileType) === FileType.ExpiringProgram)
    .map((file) => file.id);

  const expiringEndorsementFiles = submissionFiles
    .filter((file) => getFileMetadataField(file, BoxTemplateLabels.FileType) === FileType.ExpiringEndorsement)
    .map((file) => file.id);

  const handleCalculateActualPremium = () => {
    triggerAIAssistant({
      fileIds: [...expiringPolicyFiles, ...expiringEndorsementFiles],
      aiFeature: AIFeature.ActualPremiumCalculation,
    });
  };

  const hasActualPremiumResponse = !!expiringPolicyData.actualPremiumResponse;

  const isCalculateButtonDisabled =
    isLoadingSubmissionFiles || expiringPolicyFiles.length === 0 || expiringEndorsementFiles.length === 0;

  const defaultValues = useMemo(() => {
    const getDefaultValues = (data: Partial<SubmissionExpiringPolicyData>): ExpiringPolicyFormData => ({
      insuranceProductId: data.insuranceProductId || '',
      effectiveDate: data.effectiveDate,
      premium: data.premium?.toString() || '',
      policyNumber: data.policyNumber || '',
      aggregateLimit: data.aggregateLimit?.toString() || '',
      perOccurrenceLimit: data.perOccurrenceLimit?.toString() || '',
      deductibles: data.deductibles?.toString() || '',
    });
    return getDefaultValues(expiringPolicyData);
  }, [expiringPolicyData]);

  const { methods } = useFormProvider<ExpiringPolicyFormData>({
    defaultValues,
    schema,
    setIsDirty,
  });

  return (
    <DirtyRouteGuard isDirty={isDirty}>
      <FormProvider {...methods}>
        <InnerPageLayout
          title="Expiring Policy"
          sx={{ padding: 3, overflow: 'hidden' }}
          footer={(innerPageProps) => (
            <ExpiringPolicyFooter
              {...innerPageProps}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
              setIsDirty={setIsDirty}
              methods={methods}
            />
          )}
        >
          {() => (
            <Stack height={1} maxWidth={640} mx="auto">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ProductSelect
                    initialProductId={defaultValues.insuranceProductId}
                    selectProducts={selectProducts}
                    isLoading={productsLoading}
                    showAllSubmissionProducts
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormDatePicker
                    label={formFieldsConfig.effectiveDate.label}
                    name={formFieldsConfig.effectiveDate.name}
                    id={formFieldsConfig.effectiveDate.id}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="row" gap={1}>
                    <FormTextField
                      label={formFieldsConfig.premium.label}
                      name={formFieldsConfig.premium.name}
                      id={formFieldsConfig.premium.id}
                      isNumberFormat
                      numberFormatProps={{
                        min: 1,
                      }}
                    />
                    {hasActualPremiumResponse && (
                      <Box display="flex" alignSelf="center">
                        <IconButton
                          icon={AutoFixHighOutlined}
                          size="medium"
                          onClick={handleCalculateActualPremium}
                          disabled={isCalculateButtonDisabled}
                          variant="contained"
                          color="success"
                          tooltipContent={
                            isCalculateButtonDisabled
                              ? messages.submissionWorkspace.expiringPolicy.calculateActualPremiumDisabledTooltip
                              : messages.submissionWorkspace.expiringPolicy.calculateActualPremium
                          }
                        />
                      </Box>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <FormTextField
                    label={formFieldsConfig.policyNumber.label}
                    name={formFieldsConfig.policyNumber.name}
                    id={formFieldsConfig.policyNumber.id}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormTextField
                    label={formFieldsConfig.aggregateLimit.label}
                    name={formFieldsConfig.aggregateLimit.name}
                    id={formFieldsConfig.aggregateLimit.id}
                    isNumberFormat
                    numberFormatProps={{
                      min: 1,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormTextField
                    label={formFieldsConfig.perOccurrenceLimit.label}
                    name={formFieldsConfig.perOccurrenceLimit.name}
                    id={formFieldsConfig.perOccurrenceLimit.id}
                    optional
                    isNumberFormat
                    numberFormatProps={{
                      min: 1,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormTextField
                    label={formFieldsConfig.deductibles.label}
                    name={formFieldsConfig.deductibles.name}
                    id={formFieldsConfig.deductibles.id}
                    isNumberFormat
                    numberFormatProps={{
                      min: 0,
                    }}
                  />
                </Grid>
              </Grid>
            </Stack>
          )}
        </InnerPageLayout>
      </FormProvider>
    </DirtyRouteGuard>
  );
}
